import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';

import { UserManagementService } from './services/user-management.service';
import { User } from './models/user.model';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import {
  GridColumn,
  GridSort,
  gridColumns,
} from '../../shared/components/grid/models/grid.model';
import { PageableApiResponse } from '../api-response';
import { CreateUserDialogComponent, DialogMode } from './create-user-dialog/create-user-dialog.component';
import { ContextService } from '../../core/context.service';
import { ToastService } from '../../shared/services/toast-service';
import { Organization } from '../../core/models/organization.model';
import { LabelBadge } from '../../shared/components/label-badge/label-badge';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoDirective,
    Grid,
    GridPage,
    GridCellDirective,
    GridDetailDirective,
    CreateUserDialogComponent,
    LabelBadge,
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserManagementComponent implements OnInit {
  private readonly userManagementService = inject(UserManagementService);
  private readonly contextService = inject(ContextService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  users = signal<User[]>([]);
  page = signal(0);
  size = signal(10);
  totalElements = signal(0);
  totalPages = signal(0);
  sort = signal<GridSort>({ sortBy: 'email', sortDirection: 'ASC' });
  selectedIds = signal<readonly string[]>([]);
  detailUser = signal<User | null>(null);
  isLoading = signal(false);

  isUserDialogOpen = signal(false);
  dialogMode = signal<DialogMode>('add');
  currentUser = signal<User | null>(null);
  selectedTenantIds = signal<string[]>([]);
  organizations = signal<Organization[]>([]);

  /** Stable row id for selection + tracking. */
  readonly userId = (u: User) => u.id;

  /**
   * Narrows the `unknown` row that projected grid templates receive back to
   * User. Needed because Angular erases the grid's generic in this host
   * template (see the grid's generic-typing note). Use in `@let` at the top of
   * each gridCell/gridDetail template.
   */
  readonly asUser = (row: unknown): User => row as User;

  // gridColumns<User> locks the row type so value/status accessors are checked.
  readonly columns: GridColumn<User>[] = gridColumns<User>([
    // Email leads — it IS the login identifier (EmailAsUsername realm).
    { key: 'email', label: 'Email', type: 'text', textVariant: 'strong', sortable: true, alwaysVisible: true, width: 260 },
    { key: 'firstName', label: 'First Name', type: 'text', sortable: true, hideOnCompact: true },
    { key: 'lastName', label: 'Last Name', type: 'text', sortable: true, hideOnCompact: true },
    { key: 'roles', label: 'Roles', type: 'template', template: 'roles', width: 160 },
  ]);

  constructor() {
    this.contextService.organization$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(org => {
      // For now, available organizations is just the current one from JWT
      // TODO: Fetch available organizations from endpoint
      this.organizations.set(org ? [org] : []);
    });
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    const { sortBy, sortDirection } = this.sort();
    this.isLoading.set(true);
    this.userManagementService.getUsers(this.page(), this.size(), sortBy, sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: PageableApiResponse<User>) => {
          this.users.set(response.data);
          this.page.set(response.page);
          this.size.set(response.size);
          this.totalElements.set(response.totalElements);
          this.totalPages.set(response.totalPages);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to load users:', err);
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.loadUsers();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.loadUsers();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.loadUsers();
  }

  onRowClick(user: User): void {
    this.detailUser.set(user);
    this.userManagementService.getUserById(user.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((detail) => {
        this.dialogMode.set('view');
        // The dialog form only needs the row's fields; the fetch supplies the
        // organization memberships the list payload doesn't carry.
        this.currentUser.set(user);
        this.selectedTenantIds.set(detail.organizations.map((org) => org.id));
        this.isUserDialogOpen.set(true);
      });
  }

  openCreateUserDialog(): void {
    this.dialogMode.set('add');
    this.currentUser.set(null);
    this.selectedTenantIds.set([]); // Clear selected tenants for new user
    this.isUserDialogOpen.set(true);
  }

  closeUserDialog(): void {
    this.isUserDialogOpen.set(false);
  }

  handleUserSave(newUser: {
    email: string;
    firstName: string;
    lastName: string;
    organizationIds: string[];
  }): void {
    this.userManagementService.createUser(newUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('User created successfully', 'success');
          this.closeUserDialog();
          this.loadUsers();
        },
        error: (err) => {
          console.error('Failed to create user:', err);
          this.toastService.show('Failed to create user', 'error');
        }
      });
  }
}
